import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import Input from "components/input";
// Preferred currency moved to its own section; removing old select import

function EditBankDetailsInformation({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});

  const defaultsValues = {
    companyName: userDetails?.bankDetails?.companyName ?? "",
    accountName: userDetails?.bankDetails?.accountName ?? "",
    bankName: userDetails?.bankDetails?.bankName ?? "",
    accountNumber: userDetails?.bankDetails?.accountNumber ?? "",
    accountCurrency: userDetails?.bankDetails?.accountCurrency ?? "",
    swiftCode: userDetails?.bankDetails?.swiftCode ?? "",
    iban: userDetails?.bankDetails?.iban ?? "",
    bankAddress: userDetails?.bankDetails?.bankAddress ?? "",
    routingCode: userDetails?.bankDetails?.routingCode ?? "",
    sortCode: userDetails?.bankDetails?.sortCode ?? "",
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: defaultsValues });

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("bankDetails")}
        </div>
        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="grid gap-[30px] w-full p-[24px]">
        <div className="flex flex-wrap gap-[20px]">
          <Input
            label={t("companyName")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("companyName").toLowerCase(),
            })}
            name="companyName"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("companyName") }),
            }}
            errorMessage={errors.companyName ? errors.companyName.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.companyName ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("accountHolderName")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("accountHolderName").toLowerCase(),
            })}
            name="accountName"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("accountHolderName") }),
            }}
            errorMessage={errors.accountName ? errors.accountName.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.accountName ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("bankName")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("bankName").toLowerCase(),
            })}
            name="bankName"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("bankName") }),
            }}
            errorMessage={errors.bankName ? errors.bankName.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.bankName ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("accountNumber")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("accountNumber").toLowerCase(),
            })}
            name="accountNumber"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("accountNumber") }),
            }}
            errorMessage={
              errors.accountNumber ? errors.accountNumber.message : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.accountNumber ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("accountCurrency")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("accountCurrency").toLowerCase(),
            })}
            name="accountCurrency"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("accountCurrency") }),
            }}
            errorMessage={
              errors.accountCurrency ? errors.accountCurrency.message : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.accountCurrency ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("swiftCode")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("swiftCode").toLowerCase(),
            })}
            name="swiftCode"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("swiftCode") }),
            }}
            errorMessage={errors.swiftCode ? errors.swiftCode.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.swiftCode ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("iban")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("iban").toLowerCase(),
            })}
            name="iban"
            register={register}
            registerArgs={{ required: t("errorField", { field: t("iban") }) }}
            errorMessage={errors.iban ? errors.iban.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.iban ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("bankAddress")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("bankAddress").toLowerCase(),
            })}
            name="bankAddress"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("bankAddress") }),
            }}
            errorMessage={errors.bankAddress ? errors.bankAddress.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.bankAddress ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("routingCode")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("routingCode").toLowerCase(),
            })}
            name="routingCode"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("routingCode") }),
            }}
            errorMessage={errors.routingCode ? errors.routingCode.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.routingCode ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("sortCode")}
            isRequired
            placeholder={t("placeHolderType", {
              title: t("sortCode").toLowerCase(),
            })}
            name="sortCode"
            register={register}
            registerArgs={{
              required: t("errorField", { field: t("sortCode") }),
            }}
            errorMessage={errors.sortCode ? errors.sortCode.message : ""}
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.sortCode ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        </div>
      </div>
    </form>
  );
}

EditBankDetailsInformation.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditBankDetailsInformation;
